import React, { FC, MutableRefObject, useState, useEffect, useRef, useCallback } from 'react';
import Alert from 'react-bootstrap/Alert';
import CSSTransition from 'react-transition-group/CSSTransition';

export type AlertMessageType = 'danger' | 'warning' | 'success';

export interface Props {
  clearMessages?: () => void;
  manualDismiss?: boolean;
  message: string;
  messageType?: AlertMessageType;
  onAlertDismiss?: () => void;
  timeout?: boolean;
  visible?: boolean;
}

export const AlertMessage: FC<Props> = ({
  clearMessages = () => {},
  manualDismiss,
  message,
  messageType,
  onAlertDismiss = () => {},
  timeout = true,
  visible = true,
}) => {
  const [alertVisible, setAlertVisible] = useState<boolean>(true);
  const [hasAlert, setHasAlert] = useState<boolean>(false);
  const timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | undefined> = useRef();

  const dismissAlert = useCallback((): void => {
    timeoutRef.current = undefined;
    setAlertVisible(false);
    setHasAlert(false);
    clearMessages();
  }, [clearMessages]);

  useEffect(() => {
    if (!manualDismiss && message) {
      timeoutRef.current = setTimeout(dismissAlert, 4000);
      setAlertVisible(true);

      return () => clearTimeout(timeoutRef.current);
    }
    // If dismissAlert is added in the dependencies,
    // the alert keeps popping up as this effect is ran over and over
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDismiss, message]);

  useEffect(() => {
    if (message && !manualDismiss && !timeout) {
      timeoutRef.current = setTimeout(dismissAlert, 4000);
      setAlertVisible(true);
      setHasAlert(document.getElementsByClassName('alert')?.length > 0);
    }
  }, [message, manualDismiss, timeout, dismissAlert]);

  const manualDismissFunc = (): void => {
    clearMessages();
    onAlertDismiss();
  };

  if (!message || hasAlert) return null;

  if (manualDismiss) {
    return (
      <Alert variant={messageType} onClose={manualDismissFunc} dismissible={true}>
        <strong>{message}</strong>
      </Alert>
    );
  }

  if (!visible || !alertVisible) return null;

  const onDismissFunc = timeout ? undefined : onAlertDismiss || dismissAlert;

  return (
    <CSSTransition classNames="alert-transition" timeout={1000}>
      <Alert variant={messageType} onClose={onDismissFunc} transition={false}>
        <strong>{message}</strong>
      </Alert>
    </CSSTransition>
  );
};

AlertMessage.displayName = 'AlertMessage';
